/**
 * Created by maksymkunytsia on 10/23/16.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  NgModule,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MenuController, NavController } from '@ionic/angular';
import { Store } from '../../classes/store.class';
import { Employee } from '../../classes/employee.class';
import { UPDATES_TYPES } from '../../constants/updates-types.const';
import { SecurityService } from '@core/services/system/security.service';
import { UpdatesService } from '@core/services/system/updates.service';
import { IMAGE_SIZES } from '../../classes/image.class';
import { LogService } from '@core/services/system/logger/log.service';
import { ROUTE_URLS } from '@routing/route-urls.const';
import { UpdateTypeInterface } from '@shared/interfaces/update-type.interface';
import { RouteNavigationService } from '@core/services/system/route-navigation/route-navigation.service';
import { PersonPlaceholderModule } from '../person-placeholder/person-placeholder.component';
import { ImageLoaderCapComponentModule } from '@shared/components';
import { ImageUrlPipeModule } from '@shared/pipes';
import { CommonModule } from '@angular/common';
import { EmployeesFacadeStore } from '@state/employees';
import { Observable } from 'rxjs';

@Component({
  selector: 'current-user',
  templateUrl: './current-user.component.html',
  styleUrls: ['./current-user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentUser {
  @Input() showStore = true;
  public IMAGE_SIZES: any = IMAGE_SIZES;
  public ActiveStore: Store = null;
  public requiredUpdatesTypes: UpdateTypeInterface[] = [UPDATES_TYPES.Store, UPDATES_TYPES.Employee];
  private shouldShowStore = false;

  public ActiveEmployee: Observable<Employee> = this.employeesStateFacade.activeEmployee$;

  private readonly logger = this.logService.createLogger('CurrentUser');

  constructor(
    private securityService: SecurityService,
    private navController: NavController,
    private updatesService: UpdatesService,
    private menuController: MenuController,
    private cdr: ChangeDetectorRef,
    private routeNavigationService: RouteNavigationService,
    private logService: LogService,
    private employeesStateFacade: EmployeesFacadeStore
  ) {
    this.initStoreUpdateEffects();
    this.initEmployeeUpdateEffect();
    this.initActiveStoreEffect();
    this.initStoreListEffect();
  }

  private initStoreUpdateEffects(): void {
    const storeUpdatesSignal = toSignal(this.updatesService.updatesEmitters[UPDATES_TYPES.Store.type] as EventEmitter<any[]>, {
      initialValue: undefined,
    });
    effect(
      () => {
        const data = storeUpdatesSignal();
        if (!data?.length) return;
        this.handleUpdateData(data, UPDATES_TYPES.Store.type);
      },
      { allowSignalWrites: true }
    );
  }

  private initEmployeeUpdateEffect(): void {
    const employeeUpdatesSignal = toSignal(this.updatesService.updatesEmitters[UPDATES_TYPES.Employee.type] as EventEmitter<any[]>, {
      initialValue: undefined,
    });
    effect(() => {
      const data = employeeUpdatesSignal();
      if (!data?.length) return;
      this.handleUpdateData(data, UPDATES_TYPES.Employee.type);
    });
  }

  private initActiveStoreEffect(): void {
    effect(() => {
      const store = this.securityService.activeStoreUpdate();
      if (store === undefined) return;
      this.setActiveStore(store);
      this.cdr.markForCheck();
    });
  }

  private initStoreListEffect(): void {
    const storeListSignal = toSignal(this.securityService.getStoreList(), { initialValue: undefined });
    effect(() => {
      const stores = storeListSignal();
      if (stores === undefined) return;
      this.setShowStore(stores);
      this.cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.shouldShowStore = this.showStore;
    this.routeNavigationService.isInitializationFinished().then(() => {
      this.setActiveStore(this.securityService.getActiveStore());
      const stores = this.securityService.getStoreList().getValue();
      this.setShowStore(stores);
      this.cdr.markForCheck();
    });
  }

  private setActiveStore(store: Store) {
    this.ActiveStore = store ? new Store(store) : null;
  }

  private setShowStore(stores: Store[]) {
    if (!this.shouldShowStore) {
      return;
    }

    this.showStore = false;
    if (stores && stores.length > 1) {
      this.showStore = true;
    }
  }

  public handleUpdateData(updateData, type) {
    if (type !== UPDATES_TYPES.Store.type || !this.ActiveStore) {
      return;
    }

    const isActiveStoreDeleted = updateData.some((item) => item?.data?.uuid === this.ActiveStore.uuid && item?.data?.deleted);
    if (isActiveStoreDeleted) {
      this.logout();
      this.securityService.setActiveStore(null);
    }
  }

  public logout() {
    this.menuController.close().catch((err) => this.logger.error(err, 'logout:MenuController:close'));
    this.employeesStateFacade.clearActiveEmployee();
    this.navController
      .navigateRoot(ROUTE_URLS.employee, { animated: true, animationDirection: 'back' })
      .catch((err) => this.logger.error(err, 'logout:navController:navigateRoot:employee'));
  }
}
@NgModule({
  imports: [CommonModule, PersonPlaceholderModule, ImageLoaderCapComponentModule, ImageUrlPipeModule],
  declarations: [CurrentUser],
  exports: [CurrentUser],
})
export class CurrentUserModule {}
